/**
 * **Real drive minutes between seeded addresses** (Pete, 2026-10-12). The seed
 * prices a drive from territory to territory; the product recomputes the real
 * one whenever a route is updated, so a seeded day read one way before a move
 * and another after it. Pete updated routes on a local tenant with the real
 * drives, and `seed/harvestDrives.ts` read them back into this table.
 *
 * A key is a leg `from>to`, each end a patient's ref (a ref keeps one address)
 * or a clinician's home (`@dana`), so a leg is true whichever day it falls on.
 * The seed places by these minutes and writes them onto its drive bands ahead
 * of its estimate (`seed/schedule.ts`), either direction, and pushes a
 * hand-laid visit later where a real drive does not fit in front of it.
 *
 * Two writers merge into it and neither forgets a leg: `seed/harvestDrives.ts`
 * (the minutes the product wrote when a route was updated; they win) and the
 * service's `scripts/homeHealthDemoDrives.ts` (a lookup of every leg the
 * Mon–Fri seeds lay out, through the optimizer's drive-time cache). A seed
 * change that lays out a new leg falls back to the estimate until the lookup
 * runs again; already-cached legs cost nothing.
 */
export interface DriveHarvest {
  harvestedAt: string;
  /** The tenant's demo day when it was harvested. */
  demoDay: string;
  /** `handle|YYYY-MM-DD` for every clinician-day that gave a leg. */
  clinicianDays: string[];
  legs: number;
}

// --- generated by harvestDrives.ts below; do not edit by hand ---
export const DRIVE_HARVESTS: DriveHarvest[] = [
  {
    "harvestedAt": "2026-10-13T05:00:59.341Z",
    "demoDay": "2026-10-14",
    "clinicianDays": [
      "dana|2026-10-14",
      "dana|2026-10-15",
      "dana|2026-10-16",
      "marcus|2026-10-14",
      "marcus|2026-10-15",
      "marcus|2026-10-16"
    ],
    "legs": 35
  },
  {
    "harvestedAt": "2026-10-13T05:13:36.034Z",
    "demoDay": "lookup 2026-10-12–2026-10-16",
    "clinicianDays": [],
    "legs": 1022
  },
  {
    "harvestedAt": "2026-10-13T05:17:17.230Z",
    "demoDay": "lookup 2026-10-12–2026-10-16",
    "clinicianDays": [],
    "legs": 55
  },
  {
    "harvestedAt": "2026-10-13T05:17:37.081Z",
    "demoDay": "lookup 2026-10-12–2026-10-16",
    "clinicianDays": [],
    "legs": 5
  },
  {
    "harvestedAt": "2026-10-13T05:20:23.242Z",
    "demoDay": "lookup 2026-10-12–2026-10-16",
    "clinicianDays": [],
    "legs": 18
  },
  {
    "harvestedAt": "2026-10-13T05:20:33.656Z",
    "demoDay": "lookup 2026-10-12–2026-10-16",
    "clinicianDays": [],
    "legs": 5
  },
  {
    "harvestedAt": "2026-10-13T05:32:24.589Z",
    "demoDay": "lookup 2026-10-12–2026-10-16",
    "clinicianDays": [],
    "legs": 6
  },
  {
    "harvestedAt": "2026-10-13T05:37:22.405Z",
    "demoDay": "lookup 2026-10-12–2026-10-16",
    "clinicianDays": [],
    "legs": 3
  }
];

export const DRIVE_MINUTES: Record<string, number> = {
  "@ana>census-01": 35,
  "@ana>census-04": 34,
  "@ana>census-11": 28,
  "@ana>census-13": 34,
  "@ana>census-14": 36,
  "@ana>census-18": 25,
  "@ana>census-19": 21,
  "@ana>census-21": 27,
  "@ana>census-23": 10,
  "@ana>census-24": 16,
  "@ana>census-26": 10,
  "@ana>census-36": 38,
  "@ana>census-38": 36,
  "@ana>census-42": 11,
  "@ana>census-46": 11,
  "@ana>census-51": 15,
  "@ana>census-52": 33,
  "@ana>census-56": 32,
  "@ana>census-58": 39,
  "@ana>census-61": 28,
  "@ana>census-62": 10,
  "@ana>census-66": 11,
  "@ana>census-68": 34,
  "@ana>census-71": 10,
  "@ana>census-72": 29,
  "@ana>census-78": 39,
  "@ana>census-82": 10,
  "@dana>census-04": 1,
  "@dana>census-23": 44,
  "@dana>census-26": 43,
  "@dana>census-30": 12,
  "@dana>census-35": 4,
  "@dana>census-42": 46,
  "@dana>census-45": 7,
  "@dana>census-46": 34,
  "@dana>census-50": 13,
  "@dana>census-55": 5,
  "@dana>census-62": 43,
  "@dana>census-63": 6,
  "@dana>census-66": 46,
  "@dana>census-70": 8,
  "@dana>census-74": 5,
  "@dana>census-82": 40,
  "@dana>census-83": 5,
  "@dana>census-85": 8,
  "@dana>osei": 17,
  "@elena>census-24": 19,
  "@jordan>census-04": 23,
  "@kim>census-04": 22,
  "@kim>census-14": 13,
  "@kim>census-15": 11,
  "@kim>census-17": 22,
  "@kim>census-20": 8,
  "@kim>census-22": 18,
  "@kim>census-24": 17,
  "@kim>census-25": 22,
  "@kim>census-37": 22,
  "@kim>census-41": 16,
  "@kim>census-44": 11,
  "@kim>census-49": 16,
  "@kim>census-51": 16,
  "@kim>census-57": 19,
  "@kim>census-61": 5,
  "@kim>census-69": 11,
  "@kim>census-71": 15,
  "@kim>census-77": 18,
  "@kim>census-81": 11,
  "@kim>census-84": 2,
  "@luis>census-27": 20,
  "@luis>census-34": 24,
  "@luis>census-35": 26,
  "@luis>census-39": 15,
  "@luis>census-45": 31,
  "@luis>census-47": 26,
  "@luis>census-50": 29,
  "@luis>census-54": 32,
  "@luis>census-59": 22,
  "@luis>census-60": 28,
  "@luis>census-67": 26,
  "@luis>census-70": 33,
  "@luis>census-73": 26,
  "@luis>census-75": 26,
  "@luis>census-85": 28,
  "@marcus>census-07": 21,
  "@marcus>census-14": 4,
  "@marcus>census-15": 2,
  "@marcus>census-16": 5,
  "@marcus>census-17": 25,
  "@marcus>census-19": 19,
  "@marcus>census-20": 14,
  "@marcus>census-21": 11,
  "@marcus>census-27": 19,
  "@marcus>census-29": 16,
  "@marcus>census-36": 6,
  "@marcus>census-44": 3,
  "@marcus>census-47": 24,
  "@marcus>census-49": 11,
  "@marcus>census-52": 7,
  "@marcus>census-53": 19,
  "@marcus>census-56": 10,
  "@marcus>census-59": 23,
  "@marcus>census-61": 7,
  "@marcus>census-64": 3,
  "@marcus>census-67": 25,
  "@marcus>census-69": 4,
  "@marcus>census-72": 10,
  "@marcus>census-73": 24,
  "@marcus>census-84": 11,
  "@marcus>ferreira": 7,
  "@marcus>okafor": 5,
  "@priya>census-22": 25,
  "@priya>census-24": 23,
  "@priya>census-25": 28,
  "@priya>census-37": 29,
  "@priya>census-51": 22,
  "@priya>census-57": 25,
  "@priya>census-71": 21,
  "@priya>census-77": 25,
  "@rachel>census-04": 5,
  "@rachel>census-13": 28,
  "@rachel>census-14": 27,
  "@rachel>census-18": 34,
  "@rachel>census-19": 28,
  "@rachel>census-23": 40,
  "@rachel>census-24": 33,
  "@rachel>census-38": 7,
  "@rachel>census-41": 17,
  "@rachel>census-48": 16,
  "@rachel>census-51": 32,
  "@rachel>census-58": 8,
  "@rachel>census-68": 13,
  "@rachel>census-71": 31,
  "@rachel>census-78": 10,
  "@rachel>census-81": 30,
  "@sam>census-11": 22,
  "@sam>census-19": 17,
  "@sam>census-23": 14,
  "@sam>census-29": 23,
  "@sam>census-51": 6,
  "@sam>census-61": 23,
  "@sam>census-81": 28,
  "@tasha>census-15": 20,
  "@tasha>census-20": 23,
  "@tasha>census-25": 24,
  "@tasha>census-40": 14,
  "@tasha>census-50": 16,
  "@tasha>census-60": 18,
  "@tasha>census-70": 22,
  "@tasha>census-80": 15,
  "census-01>@dana": 4,
  "census-01>census-04": 3,
  "census-01>census-11": 17,
  "census-01>census-14": 23,
  "census-01>census-26": 38,
  "census-01>census-28": 24,
  "census-01>census-30": 12,
  "census-01>census-35": 6,
  "census-01>census-36": 24,
  "census-01>census-42": 41,
  "census-01>census-46": 28,
  "census-01>census-51": 31,
  "census-01>census-62": 38,
  "census-01>census-66": 40,
  "census-01>census-68": 16,
  "census-01>census-78": 8,
  "census-04>@rachel": 5,
  "census-04>census-11": 15,
  "census-04>census-13": 24,
  "census-04>census-14": 21,
  "census-04>census-18": 30,
  "census-04>census-19": 26,
  "census-04>census-21": 19,
  "census-04>census-23": 37,
  "census-04>census-24": 29,
  "census-04>census-25": 35,
  "census-04>census-26": 36,
  "census-04>census-28": 22,
  "census-04>census-35": 4,
  "census-04>census-36": 22,
  "census-04>census-38": 5,
  "census-04>census-40": 3,
  "census-04>census-41": 13,
  "census-04>census-44": 22,
  "census-04>census-45": 6,
  "census-04>census-46": 26,
  "census-04>census-48": 15,
  "census-04>census-49": 16,
  "census-04>census-50": 14,
  "census-04>census-51": 29,
  "census-04>census-52": 22,
  "census-04>census-58": 4,
  "census-04>census-61": 25,
  "census-04>census-62": 36,
  "census-04>census-65": 4,
  "census-04>census-66": 38,
  "census-04>census-68": 14,
  "census-04>census-71": 27,
  "census-04>census-72": 21,
  "census-04>census-76": 25,
  "census-04>census-77": 30,
  "census-04>census-78": 5,
  "census-04>census-81": 25,
  "census-04>census-82": 32,
  "census-07>census-12": 16,
  "census-07>ferreira": 26,
  "census-11>@ana": 30,
  "census-11>@dana": 21,
  "census-11>census-13": 23,
  "census-11>census-14": 21,
  "census-11>census-16": 23,
  "census-11>census-18": 25,
  "census-11>census-26": 25,
  "census-11>census-34": 10,
  "census-11>census-35": 16,
  "census-11>census-40": 15,
  "census-11>census-42": 26,
  "census-11>census-43": 18,
  "census-11>census-46": 15,
  "census-11>census-55": 18,
  "census-11>census-60": 18,
  "census-11>census-65": 18,
  "census-11>census-66": 25,
  "census-12>@marcus": 29,
  "census-12>census-20": 23,
  "census-12>census-52": 23,
  "census-12>census-69": 24,
  "census-13>@ana": 33,
  "census-13>@rachel": 21,
  "census-13>@sam": 28,
  "census-13>census-14": 5,
  "census-13>census-16": 6,
  "census-13>census-18": 15,
  "census-13>census-29": 18,
  "census-13>census-61": 10,
  "census-13>census-71": 25,
  "census-13>census-78": 24,
  "census-14>@ana": 33,
  "census-14>@kim": 13,
  "census-14>@marcus": 4,
  "census-14>@rachel": 19,
  "census-14>census-16": 7,
  "census-14>census-17": 28,
  "census-14>census-18": 19,
  "census-14>census-19": 22,
  "census-14>census-21": 11,
  "census-14>census-23": 34,
  "census-14>census-24": 27,
  "census-14>census-26": 33,
  "census-14>census-36": 4,
  "census-14>census-42": 36,
  "census-14>census-48": 24,
  "census-14>census-59": 23,
  "census-14>census-61": 10,
  "census-14>census-71": 25,
  "census-14>census-77": 28,
  "census-15>@kim": 12,
  "census-15>@tasha": 23,
  "census-15>census-16": 6,
  "census-15>census-20": 16,
  "census-15>census-21": 12,
  "census-15>census-25": 32,
  "census-15>census-37": 33,
  "census-15>census-50": 23,
  "census-15>census-71": 25,
  "census-16>@ana": 28,
  "census-16>@marcus": 5,
  "census-16>census-17": 25,
  "census-16>census-18": 20,
  "census-16>census-19": 18,
  "census-16>census-20": 17,
  "census-16>census-21": 7,
  "census-16>census-23": 29,
  "census-16>census-27": 13,
  "census-16>census-36": 9,
  "census-16>census-42": 31,
  "census-16>census-44": 6,
  "census-16>census-52": 3,
  "census-16>census-56": 7,
  "census-16>census-59": 19,
  "census-16>census-68": 24,
  "census-17>@kim": 24,
  "census-17>@marcus": 26,
  "census-17>census-19": 18,
  "census-17>census-21": 24,
  "census-17>census-57": 23,
  "census-17>census-67": 27,
  "census-17>census-77": 23,
  "census-18>@ana": 22,
  "census-18>@rachel": 29,
  "census-18>census-19": 11,
  "census-18>census-21": 20,
  "census-18>census-23": 26,
  "census-18>census-24": 19,
  "census-18>census-36": 22,
  "census-18>census-38": 29,
  "census-18>census-41": 23,
  "census-18>census-42": 27,
  "census-18>census-46": 16,
  "census-18>census-58": 30,
  "census-19>@ana": 17,
  "census-19>@jordan": 7,
  "census-19>@kim": 12,
  "census-19>@rachel": 24,
  "census-19>@sam": 12,
  "census-19>census-07": 20,
  "census-19>census-21": 16,
  "census-19>census-23": 22,
  "census-19>census-24": 14,
  "census-19>census-36": 24,
  "census-19>census-38": 24,
  "census-19>census-41": 18,
  "census-19>census-46": 11,
  "census-19>census-48": 30,
  "census-19>census-52": 22,
  "census-19>census-58": 25,
  "census-19>census-71": 12,
  "census-19>census-76": 19,
  "census-19>census-78": 27,
  "census-19>census-81": 20,
  "census-20>@kim": 11,
  "census-20>@marcus": 16,
  "census-20>@tasha": 28,
  "census-20>census-07": 22,
  "census-20>census-21": 18,
  "census-20>census-22": 17,
  "census-20>census-25": 21,
  "census-20>census-27": 19,
  "census-20>census-36": 20,
  "census-20>census-37": 22,
  "census-20>census-40": 25,
  "census-20>census-50": 28,
  "census-20>census-52": 20,
  "census-20>census-56": 23,
  "census-20>census-70": 30,
  "census-20>census-71": 14,
  "census-21>@ana": 22,
  "census-21>@marcus": 13,
  "census-21>@sam": 17,
  "census-21>census-07": 12,
  "census-21>census-23": 25,
  "census-21>census-24": 18,
  "census-21>census-27": 9,
  "census-21>census-36": 13,
  "census-21>census-38": 15,
  "census-21>census-39": 9,
  "census-21>census-41": 8,
  "census-21>census-47": 13,
  "census-21>census-52": 6,
  "census-21>census-53": 11,
  "census-21>census-56": 9,
  "census-21>census-59": 14,
  "census-21>census-67": 14,
  "census-22>@kim": 23,
  "census-22>@priya": 28,
  "census-22>census-15": 31,
  "census-22>census-25": 6,
  "census-22>census-37": 8,
  "census-22>census-69": 28,
  "census-22>census-71": 10,
  "census-23>@ana": 11,
  "census-23>@rachel": 38,
  "census-23>census-04": 35,
  "census-23>census-11": 27,
  "census-23>census-19": 22,
  "census-23>census-24": 13,
  "census-23>census-26": 5,
  "census-23>census-28": 28,
  "census-23>census-29": 26,
  "census-23>census-36": 38,
  "census-23>census-38": 37,
  "census-23>census-41": 31,
  "census-23>census-46": 14,
  "census-23>census-48": 36,
  "census-23>census-51": 20,
  "census-23>census-56": 33,
  "census-23>census-58": 38,
  "census-23>census-61": 28,
  "census-23>census-68": 34,
  "census-23>census-78": 39,
  "census-23>census-81": 34,
  "census-23>osei": 33,
  "census-24>@ana": 16,
  "census-24>@elena": 29,
  "census-24>@rachel": 32,
  "census-24>census-26": 9,
  "census-24>census-28": 22,
  "census-24>census-29": 20,
  "census-24>census-36": 31,
  "census-24>census-38": 30,
  "census-24>census-41": 24,
  "census-24>census-44": 28,
  "census-24>census-48": 30,
  "census-24>census-49": 25,
  "census-24>census-51": 6,
  "census-24>census-57": 9,
  "census-24>census-61": 22,
  "census-24>census-68": 27,
  "census-24>census-71": 10,
  "census-24>census-78": 33,
  "census-24>census-81": 28,
  "census-25>@priya": 30,
  "census-25>@tasha": 27,
  "census-25>census-37": 3,
  "census-25>census-40": 33,
  "census-25>census-51": 17,
  "census-25>census-64": 32,
  "census-25>census-69": 29,
  "census-26>@ana": 11,
  "census-26>@dana": 40,
  "census-26>census-28": 28,
  "census-26>census-34": 36,
  "census-26>census-35": 35,
  "census-26>census-36": 37,
  "census-26>census-38": 36,
  "census-26>census-40": 34,
  "census-26>census-42": 5,
  "census-26>census-43": 37,
  "census-26>census-46": 14,
  "census-26>census-50": 39,
  "census-26>census-51": 14,
  "census-26>census-52": 33,
  "census-26>census-55": 37,
  "census-26>census-56": 32,
  "census-26>census-60": 37,
  "census-26>census-62": 2,
  "census-26>census-65": 37,
  "census-26>census-66": 4,
  "census-26>census-68": 33,
  "census-26>census-81": 33,
  "census-27>census-07": 13,
  "census-27>census-29": 9,
  "census-27>census-34": 13,
  "census-27>census-36": 15,
  "census-27>census-40": 15,
  "census-27>census-61": 16,
  "census-27>census-69": 14,
  "census-28>@ana": 25,
  "census-28>census-29": 14,
  "census-28>census-36": 25,
  "census-28>census-41": 17,
  "census-28>census-42": 26,
  "census-28>census-46": 15,
  "census-28>census-51": 18,
  "census-28>census-52": 22,
  "census-28>census-81": 28,
  "census-29>@ana": 24,
  "census-29>@rachel": 16,
  "census-29>@sam": 19,
  "census-29>census-07": 13,
  "census-29>census-34": 16,
  "census-29>census-35": 15,
  "census-29>census-36": 19,
  "census-29>census-38": 16,
  "census-29>census-40": 15,
  "census-29>census-43": 18,
  "census-29>census-45": 20,
  "census-29>census-46": 15,
  "census-29>census-48": 23,
  "census-29>census-51": 18,
  "census-29>census-52": 13,
  "census-29>census-53": 6,
  "census-29>census-61": 15,
  "census-29>census-71": 16,
  "census-29>census-78": 20,
  "census-30>census-01": 12,
  "census-30>census-11": 16,
  "census-30>census-26": 37,
  "census-30>census-34": 11,
  "census-30>census-42": 40,
  "census-30>census-46": 25,
  "census-30>census-75": 4,
  "census-30>census-80": 5,
  "census-30>osei": 15,
  "census-34>@dana": 12,
  "census-34>@luis": 30,
  "census-34>census-35": 11,
  "census-34>census-40": 10,
  "census-34>census-45": 14,
  "census-34>census-46": 20,
  "census-34>census-47": 12,
  "census-35>census-40": 1,
  "census-35>census-42": 39,
  "census-35>census-43": 7,
  "census-35>census-46": 26,
  "census-35>census-50": 12,
  "census-35>census-54": 14,
  "census-35>census-59": 26,
  "census-35>census-77": 31,
  "census-35>census-83": 7,
  "census-35>census-85": 5,
  "census-35>osei": 13,
  "census-36>@ana": 36,
  "census-36>@marcus": 7,
  "census-36>census-07": 17,
  "census-36>census-16": 9,
  "census-36>census-38": 20,
  "census-36>census-39": 19,
  "census-36>census-41": 12,
  "census-36>census-42": 37,
  "census-36>census-44": 4,
  "census-36>census-46": 24,
  "census-36>census-48": 25,
  "census-36>census-49": 10,
  "census-36>census-52": 9,
  "census-36>census-53": 22,
  "census-36>census-56": 8,
  "census-36>census-61": 12,
  "census-36>census-66": 37,
  "census-36>census-67": 22,
  "census-36>census-72": 14,
  "census-37>@kim": 27,
  "census-37>@priya": 35,
  "census-37>census-61": 29,
  "census-37>census-69": 32,
  "census-37>census-77": 14,
  "census-37>census-84": 25,
  "census-38>@ana": 45,
  "census-38>@rachel": 7,
  "census-38>census-14": 23,
  "census-38>census-41": 14,
  "census-38>census-42": 40,
  "census-38>census-46": 28,
  "census-38>census-48": 13,
  "census-38>census-51": 30,
  "census-38>census-52": 24,
  "census-38>census-56": 20,
  "census-38>census-58": 4,
  "census-38>census-61": 27,
  "census-38>census-62": 38,
  "census-38>census-78": 7,
  "census-39>@luis": 14,
  "census-39>@marcus": 14,
  "census-39>census-16": 9,
  "census-39>census-21": 6,
  "census-39>census-52": 12,
  "census-39>census-55": 18,
  "census-39>census-56": 10,
  "census-39>census-60": 18,
  "census-39>census-63": 19,
  "census-40>@dana": 4,
  "census-40>@luis": 40,
  "census-40>@tasha": 21,
  "census-40>census-20": 27,
  "census-40>census-43": 7,
  "census-40>census-45": 9,
  "census-40>census-46": 26,
  "census-40>census-50": 12,
  "census-40>census-60": 7,
  "census-40>census-66": 39,
  "census-40>census-70": 10,
  "census-40>census-80": 9,
  "census-40>census-85": 6,
  "census-41>@ana": 27,
  "census-41>@kim": 17,
  "census-41>@marcus": 17,
  "census-41>@rachel": 12,
  "census-41>@sam": 22,
  "census-41>census-07": 10,
  "census-41>census-14": 12,
  "census-41>census-16": 14,
  "census-41>census-23": 28,
  "census-41>census-29": 11,
  "census-41>census-42": 29,
  "census-41>census-44": 13,
  "census-41>census-46": 17,
  "census-41>census-48": 18,
  "census-41>census-49": 7,
  "census-41>census-51": 20,
  "census-41>census-52": 13,
  "census-41>census-56": 9,
  "census-41>census-58": 15,
  "census-41>census-59": 17,
  "census-41>census-61": 16,
  "census-41>census-62": 27,
  "census-41>census-72": 12,
  "census-41>census-81": 16,
  "census-42>@ana": 12,
  "census-42>@dana": 42,
  "census-42>census-11": 28,
  "census-42>census-23": 4,
  "census-42>census-24": 14,
  "census-42>census-26": 5,
  "census-42>census-43": 40,
  "census-42>census-46": 16,
  "census-42>census-48": 37,
  "census-42>census-50": 41,
  "census-42>census-54": 39,
  "census-42>census-55": 40,
  "census-42>census-56": 35,
  "census-42>census-60": 40,
  "census-42>census-62": 7,
  "census-42>census-63": 41,
  "census-42>census-66": 2,
  "census-42>census-71": 14,
  "census-42>census-83": 41,
  "census-43>@dana": 5,
  "census-43>@luis": 43,
  "census-43>census-45": 4,
  "census-43>census-46": 30,
  "census-43>census-55": 3,
  "census-43>census-66": 42,
  "census-43>census-67": 22,
  "census-43>census-85": 11,
  "census-44>@kim": 13,
  "census-44>@marcus": 3,
  "census-44>census-49": 10,
  "census-45>@dana": 7,
  "census-45>@luis": 42,
  "census-45>census-46": 32,
  "census-45>census-53": 29,
  "census-45>census-62": 42,
  "census-45>census-63": 2,
  "census-45>census-65": 4,
  "census-45>census-67": 24,
  "census-45>census-75": 15,
  "census-45>census-80": 15,
  "census-45>osei": 20,
  "census-46>@ana": 11,
  "census-46>@dana": 31,
  "census-46>census-04": 24,
  "census-46>census-42": 16,
  "census-46>census-48": 26,
  "census-46>census-50": 29,
  "census-46>census-52": 23,
  "census-46>census-54": 27,
  "census-46>census-56": 22,
  "census-46>census-58": 27,
  "census-46>census-60": 27,
  "census-46>census-62": 14,
  "census-46>census-63": 28,
  "census-46>census-65": 27,
  "census-46>census-66": 16,
  "census-46>census-68": 23,
  "census-46>census-70": 30,
  "census-46>census-82": 10,
  "census-47>@luis": 29,
  "census-47>@marcus": 32,
  "census-47>census-36": 22,
  "census-47>census-39": 15,
  "census-47>census-50": 21,
  "census-47>census-52": 20,
  "census-47>census-53": 14,
  "census-47>census-56": 20,
  "census-47>census-60": 21,
  "census-47>census-73": 1,
  "census-48>@ana": 41,
  "census-48>@rachel": 16,
  "census-48>census-18": 35,
  "census-48>census-23": 34,
  "census-48>census-51": 27,
  "census-48>census-52": 27,
  "census-48>census-56": 23,
  "census-48>census-58": 11,
  "census-48>census-61": 29,
  "census-48>census-66": 35,
  "census-48>census-68": 3,
  "census-48>census-71": 26,
  "census-48>census-72": 26,
  "census-48>census-78": 15,
  "census-48>census-81": 30,
  "census-49>@kim": 16,
  "census-49>@marcus": 12,
  "census-49>census-14": 7,
  "census-49>census-17": 25,
  "census-49>census-52": 6,
  "census-49>census-56": 5,
  "census-49>census-57": 23,
  "census-49>census-73": 15,
  "census-50>@dana": 14,
  "census-50>@tasha": 22,
  "census-50>census-15": 28,
  "census-50>census-20": 32,
  "census-50>census-53": 27,
  "census-50>census-60": 14,
  "census-50>census-62": 42,
  "census-50>census-65": 13,
  "census-50>census-66": 38,
  "census-50>census-70": 15,
  "census-50>census-74": 13,
  "census-50>census-79": 27,
  "census-50>census-80": 6,
  "census-51>@kim": 19,
  "census-51>@priya": 25,
  "census-51>@rachel": 30,
  "census-51>@sam": 6,
  "census-51>census-14": 28,
  "census-51>census-16": 23,
  "census-51>census-17": 21,
  "census-51>census-19": 15,
  "census-51>census-23": 19,
  "census-51>census-25": 17,
  "census-51>census-29": 19,
  "census-51>census-52": 27,
  "census-51>census-58": 30,
  "census-51>census-61": 21,
  "census-51>census-64": 27,
  "census-51>census-68": 26,
  "census-51>census-69": 24,
  "census-51>census-77": 7,
  "census-51>census-81": 27,
  "census-52>@ana": 28,
  "census-52>@marcus": 8,
  "census-52>census-07": 17,
  "census-52>census-18": 22,
  "census-52>census-21": 6,
  "census-52>census-36": 9,
  "census-52>census-39": 13,
  "census-52>census-53": 17,
  "census-52>census-56": 6,
  "census-52>census-58": 22,
  "census-52>census-59": 19,
  "census-52>census-61": 12,
  "census-52>census-64": 7,
  "census-52>census-66": 32,
  "census-52>census-68": 23,
  "census-52>census-72": 8,
  "census-52>census-73": 18,
  "census-52>census-76": 6,
  "census-52>census-81": 7,
  "census-53>@luis": 22,
  "census-53>census-36": 23,
  "census-53>census-56": 16,
  "census-53>census-61": 17,
  "census-53>census-72": 11,
  "census-53>census-74": 21,
  "census-53>census-79": 12,
  "census-54>@dana": 16,
  "census-54>census-59": 25,
  "census-54>census-65": 16,
  "census-54>census-82": 32,
  "census-54>census-83": 16,
  "census-54>osei": 7,
  "census-55>@dana": 4,
  "census-55>census-60": 0,
  "census-56>@ana": 30,
  "census-56>@marcus": 11,
  "census-56>census-04": 15,
  "census-56>census-07": 13,
  "census-56>census-11": 17,
  "census-56>census-14": 5,
  "census-56>census-16": 7,
  "census-56>census-18": 25,
  "census-56>census-19": 22,
  "census-56>census-21": 8,
  "census-56>census-24": 26,
  "census-56>census-26": 32,
  "census-56>census-27": 11,
  "census-56>census-36": 8,
  "census-56>census-39": 16,
  "census-56>census-52": 6,
  "census-56>census-58": 18,
  "census-56>census-59": 21,
  "census-56>census-61": 14,
  "census-56>census-62": 33,
  "census-56>census-64": 8,
  "census-56>census-66": 35,
  "census-56>census-67": 20,
  "census-56>census-68": 19,
  "census-56>census-69": 6,
  "census-56>census-72": 11,
  "census-56>census-78": 19,
  "census-56>census-79": 25,
  "census-57>@kim": 22,
  "census-57>@priya": 27,
  "census-57>census-77": 11,
  "census-57>census-81": 29,
  "census-58>@ana": 49,
  "census-58>@rachel": 7,
  "census-58>census-23": 39,
  "census-58>census-24": 32,
  "census-58>census-38": 4,
  "census-58>census-61": 27,
  "census-58>census-62": 38,
  "census-58>census-66": 41,
  "census-58>census-68": 15,
  "census-58>census-71": 30,
  "census-58>census-72": 23,
  "census-58>census-78": 4,
  "census-58>census-82": 35,
  "census-59>@luis": 22,
  "census-59>@marcus": 23,
  "census-59>census-07": 16,
  "census-59>census-16": 15,
  "census-59>census-17": 26,
  "census-59>census-21": 11,
  "census-59>census-27": 12,
  "census-59>census-36": 24,
  "census-59>census-56": 16,
  "census-59>census-67": 13,
  "census-59>census-83": 22,
  "census-60>@dana": 4,
  "census-60>@luis": 42,
  "census-60>@tasha": 24,
  "census-60>census-15": 26,
  "census-60>census-20": 30,
  "census-60>census-26": 39,
  "census-60>census-40": 6,
  "census-60>census-62": 39,
  "census-60>census-63": 1,
  "census-60>census-65": 1,
  "census-60>census-66": 42,
  "census-60>census-70": 3,
  "census-60>census-73": 21,
  "census-60>census-79": 31,
  "census-60>census-80": 13,
  "census-61>@ana": 25,
  "census-61>@jordan": 9,
  "census-61>@kim": 5,
  "census-61>@marcus": 7,
  "census-61>@rachel": 31,
  "census-61>@sam": 21,
  "census-61>census-11": 23,
  "census-61>census-13": 8,
  "census-61>census-14": 10,
  "census-61>census-15": 9,
  "census-61>census-18": 13,
  "census-61>census-20": 10,
  "census-61>census-23": 28,
  "census-61>census-29": 16,
  "census-61>census-62": 28,
  "census-61>census-64": 8,
  "census-61>census-66": 30,
  "census-61>census-68": 29,
  "census-61>census-69": 9,
  "census-61>census-71": 19,
  "census-61>census-72": 16,
  "census-61>census-76": 7,
  "census-61>census-78": 29,
  "census-61>census-79": 28,
  "census-61>census-81": 8,
  "census-62>@ana": 11,
  "census-62>@dana": 40,
  "census-62>census-11": 26,
  "census-62>census-14": 35,
  "census-62>census-24": 11,
  "census-62>census-42": 6,
  "census-62>census-63": 38,
  "census-62>census-65": 37,
  "census-62>census-66": 5,
  "census-62>census-68": 33,
  "census-62>census-70": 40,
  "census-62>census-71": 12,
  "census-62>census-72": 29,
  "census-62>census-74": 37,
  "census-62>census-75": 39,
  "census-62>census-78": 39,
  "census-62>census-80": 38,
  "census-62>census-82": 6,
  "census-62>census-83": 38,
  "census-62>osei": 33,
  "census-63>@dana": 6,
  "census-63>@luis": 41,
  "census-63>census-11": 19,
  "census-63>census-66": 43,
  "census-63>census-75": 13,
  "census-63>census-79": 32,
  "census-64>@kim": 12,
  "census-64>@marcus": 3,
  "census-64>census-67": 22,
  "census-64>census-69": 5,
  "census-65>@dana": 5,
  "census-65>census-66": 42,
  "census-65>census-70": 3,
  "census-66>@ana": 11,
  "census-66>@dana": 42,
  "census-66>census-04": 36,
  "census-66>census-11": 27,
  "census-66>census-16": 32,
  "census-66>census-18": 27,
  "census-66>census-19": 24,
  "census-66>census-23": 3,
  "census-66>census-24": 13,
  "census-66>census-26": 4,
  "census-66>census-34": 32,
  "census-66>census-42": 2,
  "census-66>census-46": 16,
  "census-66>census-70": 43,
  "census-66>census-72": 31,
  "census-66>census-74": 39,
  "census-66>census-78": 41,
  "census-66>census-82": 8,
  "census-66>census-85": 45,
  "census-67>@luis": 30,
  "census-67>@marcus": 32,
  "census-67>census-14": 21,
  "census-67>census-16": 21,
  "census-67>census-44": 23,
  "census-67>census-56": 20,
  "census-67>census-69": 22,
  "census-67>census-70": 25,
  "census-67>census-72": 17,
  "census-67>census-73": 1,
  "census-67>census-75": 18,
  "census-68>@ana": 38,
  "census-68>@rachel": 14,
  "census-68>census-11": 10,
  "census-68>census-13": 27,
  "census-68>census-14": 25,
  "census-68>census-18": 32,
  "census-68>census-19": 28,
  "census-68>census-24": 26,
  "census-68>census-36": 26,
  "census-68>census-38": 13,
  "census-68>census-48": 5,
  "census-68>census-51": 25,
  "census-68>census-66": 32,
  "census-68>census-71": 24,
  "census-68>census-72": 25,
  "census-68>census-78": 16,
  "census-68>census-81": 29,
  "census-68>census-82": 28,
  "census-69>@kim": 11,
  "census-69>@marcus": 5,
  "census-69>census-15": 5,
  "census-69>census-20": 16,
  "census-69>census-36": 9,
  "census-69>census-37": 30,
  "census-69>census-71": 21,
  "census-69>census-72": 7,
  "census-69>census-76": 4,
  "census-70>@dana": 8,
  "census-70>@luis": 43,
  "census-70>@tasha": 27,
  "census-70>census-11": 22,
  "census-70>census-15": 31,
  "census-70>census-25": 42,
  "census-70>census-40": 10,
  "census-70>census-50": 15,
  "census-70>census-60": 3,
  "census-70>census-73": 25,
  "census-70>census-74": 4,
  "census-70>census-80": 14,
  "census-70>census-82": 39,
  "census-70>census-83": 4,
  "census-70>census-85": 9,
  "census-71>@ana": 10,
  "census-71>@kim": 18,
  "census-71>@priya": 24,
  "census-71>@rachel": 29,
  "census-71>@sam": 9,
  "census-71>census-18": 17,
  "census-71>census-19": 13,
  "census-71>census-23": 13,
  "census-71>census-24": 10,
  "census-71>census-37": 13,
  "census-71>census-38": 27,
  "census-71>census-51": 12,
  "census-71>census-56": 24,
  "census-71>census-57": 8,
  "census-71>census-61": 19,
  "census-71>census-66": 14,
  "census-71>census-68": 24,
  "census-71>census-78": 30,
  "census-71>census-81": 25,
  "census-71>census-82": 8,
  "census-71>census-84": 15,
  "census-72>@ana": 22,
  "census-72>@marcus": 11,
  "census-72>census-14": 12,
  "census-72>census-15": 11,
  "census-72>census-18": 19,
  "census-72>census-19": 14,
  "census-72>census-20": 16,
  "census-72>census-21": 4,
  "census-72>census-23": 25,
  "census-72>census-24": 18,
  "census-72>census-26": 25,
  "census-72>census-27": 9,
  "census-72>census-52": 8,
  "census-72>census-61": 15,
  "census-72>census-73": 15,
  "census-72>census-76": 9,
  "census-72>census-78": 18,
  "census-72>census-79": 21,
  "census-72>census-81": 10,
  "census-72>census-84": 12,
  "census-73>@luis": 29,
  "census-73>@marcus": 32,
  "census-73>census-14": 21,
  "census-73>census-17": 26,
  "census-73>census-19": 19,
  "census-73>census-36": 22,
  "census-73>census-76": 24,
  "census-73>census-81": 25,
  "census-74>@dana": 4,
  "census-74>@luis": 42,
  "census-74>census-11": 18,
  "census-74>census-65": 1,
  "census-74>census-70": 3,
  "census-74>census-79": 31,
  "census-74>census-82": 35,
  "census-74>census-85": 7,
  "census-75>@dana": 10,
  "census-75>census-80": 3,
  "census-76>@ana": 30,
  "census-76>@marcus": 1,
  "census-76>census-07": 20,
  "census-76>census-14": 4,
  "census-76>census-16": 4,
  "census-76>census-17": 29,
  "census-76>census-18": 17,
  "census-76>census-20": 14,
  "census-76>census-36": 6,
  "census-76>census-56": 9,
  "census-76>census-61": 7,
  "census-76>census-78": 26,
  "census-76>census-79": 28,
  "census-76>census-81": 2,
  "census-76>census-82": 28,
  "census-76>census-84": 10,
  "census-77>@dana": 36,
  "census-77>@kim": 22,
  "census-77>@priya": 27,
  "census-77>census-04": 29,
  "census-77>census-22": 18,
  "census-77>census-25": 13,
  "census-77>census-51": 6,
  "census-78>@ana": 48,
  "census-78>@rachel": 9,
  "census-78>census-04": 5,
  "census-78>census-11": 20,
  "census-78>census-14": 27,
  "census-78>census-16": 29,
  "census-78>census-18": 35,
  "census-78>census-23": 42,
  "census-78>census-24": 34,
  "census-78>census-36": 27,
  "census-78>census-41": 18,
  "census-78>census-48": 13,
  "census-78>census-51": 33,
  "census-78>census-58": 4,
  "census-78>census-81": 30,
  "census-78>census-82": 37,
  "census-79>@luis": 34,
  "census-79>@marcus": 35,
  "census-79>census-15": 31,
  "census-79>census-39": 19,
  "census-79>census-47": 13,
  "census-79>census-85": 33,
  "census-80>@dana": 12,
  "census-80>@luis": 48,
  "census-80>@tasha": 22,
  "census-80>census-11": 18,
  "census-80>census-15": 28,
  "census-80>census-20": 32,
  "census-80>census-25": 35,
  "census-80>census-35": 9,
  "census-80>census-40": 9,
  "census-80>census-50": 6,
  "census-80>census-70": 14,
  "census-80>census-82": 33,
  "census-81>@ana": 32,
  "census-81>@jordan": 15,
  "census-81>@kim": 12,
  "census-81>@marcus": 1,
  "census-81>@rachel": 25,
  "census-81>@sam": 27,
  "census-81>census-11": 24,
  "census-81>census-13": 3,
  "census-81>census-14": 5,
  "census-81>census-17": 26,
  "census-81>census-18": 16,
  "census-81>census-19": 21,
  "census-81>census-23": 34,
  "census-81>census-36": 6,
  "census-81>census-38": 23,
  "census-81>census-44": 2,
  "census-81>census-46": 23,
  "census-81>census-48": 27,
  "census-81>census-49": 12,
  "census-81>census-58": 24,
  "census-81>census-62": 33,
  "census-81>census-66": 35,
  "census-81>census-71": 25,
  "census-81>census-82": 30,
  "census-81>census-84": 12,
  "census-82>@ana": 10,
  "census-82>@dana": 36,
  "census-82>census-11": 22,
  "census-82>census-14": 31,
  "census-82>census-16": 26,
  "census-82>census-18": 22,
  "census-82>census-23": 6,
  "census-82>census-24": 10,
  "census-82>census-26": 7,
  "census-82>census-36": 33,
  "census-82>census-38": 32,
  "census-82>census-42": 8,
  "census-82>census-45": 35,
  "census-82>census-55": 33,
  "census-82>census-56": 29,
  "census-82>census-62": 6,
  "census-82>census-63": 35,
  "census-82>census-68": 29,
  "census-82>census-83": 34,
  "census-82>census-85": 35,
  "census-83>@dana": 5,
  "census-83>@luis": 43,
  "census-83>census-40": 7,
  "census-83>census-45": 5,
  "census-83>osei": 19,
  "census-84>@kim": 2,
  "census-84>@marcus": 12,
  "census-84>census-16": 10,
  "census-84>census-17": 19,
  "census-84>census-24": 15,
  "census-84>census-49": 14,
  "census-84>census-52": 14,
  "census-84>census-81": 12,
  "census-85>@dana": 8,
  "census-85>@luis": 46,
  "census-85>census-01": 11,
  "census-85>census-04": 9,
  "census-85>census-11": 23,
  "census-85>census-26": 39,
  "census-85>census-34": 13,
  "census-85>census-50": 7,
  "ferreira>census-12": 28,
  "ferreira>census-14": 6,
  "ferreira>census-15": 7,
  "ferreira>census-16": 12,
  "ferreira>census-17": 28,
  "ferreira>census-21": 16,
  "ferreira>census-36": 4,
  "ferreira>census-41": 14,
  "ferreira>census-47": 24,
  "ferreira>census-52": 12,
  "ferreira>census-56": 10,
  "ferreira>census-59": 27,
  "ferreira>census-67": 24,
  "ferreira>census-72": 17,
  "ferreira>census-79": 31,
  "okafor>ferreira": 8,
  "osei>@dana": 17,
  "osei>census-01": 15,
  "osei>census-04": 13,
  "osei>census-11": 9,
  "osei>census-23": 30,
  "osei>census-26": 32,
  "osei>census-34": 14,
  "osei>census-40": 13,
  "osei>census-45": 18,
  "osei>census-46": 21,
  "osei>census-54": 8,
  "osei>census-62": 32,
  "osei>census-63": 18,
  "osei>census-65": 17,
  "osei>census-77": 26,
  "osei>census-82": 27,
};
